/**
 * Story Creation Page
 *
 * Full-screen experience for creating a personalized AI story.
 * Skips the default Layout wrapper (header/footer/nav).
 */

import { NextPage } from 'next'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { ReactElement, useEffect } from 'react'

import { usePixieWorldAuthContext } from '@/shared/auth/pixieWorldAuthContext'
import StoryCreationWizard from '@/slices/StoryCreation'

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactElement
}

const CreatePage: NextPageWithLayout = () => {
  const router = useRouter()
  const { profile, isAuthenticated, isAuthenticating } = usePixieWorldAuthContext()

  useEffect(() => {
    if (!isAuthenticating && !isAuthenticated) {
      router.replace('/')
    }

    if (isAuthenticated && profile && !profile.has_software_access) {
      router.replace('/library')
    }
  }, [isAuthenticated, isAuthenticating, profile, router])

  if (isAuthenticating || !profile || (isAuthenticated && !profile.has_software_access)) {
    return null
  }

  return (
    <>
      <Head>
        <title>Create a Story | Pixie World</title>
        <meta
          name="description"
          content="Create a personalized AI-generated storybook for your child"
        />
      </Head>
      <StoryCreationWizard />
    </>
  )
}

// Skip the default Layout wrapper for full-screen experience
CreatePage.getLayout = (page: ReactElement) => page

export default CreatePage
